import React from 'react';
import Toast from '@components/Toast';
import { inject } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps, BasePageState } from '../../types/base';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { readLinkWhitelist } from '@server';

interface SetFeedbackPostProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    thread?: ThreadAction;
    setPost?: any;
}

interface PublishSuccessOptions {
    onSuccess?: (payload: { threadId?: string | number; result: any; isEdit: boolean }) => void;
}

interface SetFeedbackPostState extends BasePageState {
    whiteList: string[];
    whiteHttpList: string[];
    editorReady: boolean;
}

const cleanWhiteHttpList = (whiteHttpList: string[]) => (
    whiteHttpList
        .filter((domain) => typeof domain === 'string')
        .map((domain) => (domain.startsWith('.') ? domain.slice(1) : domain))
);

@inject('app', 'setPost', 'site', 'user')
class SetFeedbackPost extends BasePage<SetFeedbackPostProps, SetFeedbackPostState> {
    state: SetFeedbackPostState = {
        loading: false,
        error: null,
        pageLoaded: false,
        whiteList: [],
        whiteHttpList: [],
        editorReady: false,
    };

    async componentDidMount() {
        this.onPageMount();

        const { user, app, site, setPost } = this.props;
        const platform = app?.platform || 'h5';

        if (user?.checkLogin && !user.checkLogin(platform)) {
            return;
        }

        if (site && !site.siteConfig) {
            await site.fetchSiteConfig();
        }

        const editId = new URLSearchParams(this.props.location.search).get('id');
        if (!editId && setPost?.setPostData && setPost.postData?.type !== 2) {
            setPost.setPostData({
                type: 2,
                topicIds: [],
                source: platform === 'pc' ? 'PC' : 'H5',
            });
        }

        await this.loadEditPost();
        this.onGetUrlWhitelist();
        this.setState({ editorReady: true });
    }

    componentWillUnmount() {
        const { setPost } = this.props;
        if (setPost?.clearPostData) {
            setPost.clearPostData();
        }
    }

    loadEditPost = async () => {
        const id = new URLSearchParams(this.props.location.search).get('id');
        if (!id) return;

        const { setPost } = this.props;
        if (!setPost?.fetchPostDetail) return;

        setPost.setPostData({ id });

        try {
            const detail = await setPost.fetchPostDetail(id);
            if (detail?.type == 1) {
                this.props.history?.push('/');
                return;
            }
            if (detail) {
                setPost.loadPostDetail(detail);
            }
        } catch (error) {
            console.error('[SetFeedbackPost] loadEditPost error:', error);
        }
    };

    setPostData = (data: any) => {
        this.props.setPost?.setPostData(data);
    };

    onPublishPost = async (options?: PublishSuccessOptions, coverFile?: File | null, videoFile?: File | null) => {
        const { setPost } = this.props;
        this.setState({ loading: true });
        try {
            const isEdit = !!setPost?.postData?.id;
            const result = isEdit
                ? await setPost.updatePost(coverFile, videoFile)
                : await setPost.createPost(coverFile, videoFile);
            const successMsg = isEdit ? '编辑成功' : '发布成功';
            if (result.code === 0) {
                const threadId = result?.data?.id || setPost?.postData?.id;
                if (options?.onSuccess) {
                    options.onSuccess({ threadId, result, isEdit });
                } else if (Toast) {
                    Toast.success(successMsg);
                    if (threadId) {
                        this.props.history.push('/threadDetail/' + threadId);
                    }
                } else {
                    alert(successMsg);
                }
            } else {
                const errMsg = result?.msg || result?.message || '操作失败';
                if (Toast) {
                    Toast.error(errMsg);
                } else {
                    alert(errMsg);
                }
            }
        } finally {
            this.setState({ loading: false });
        }
    };

    onGetUrlWhitelist = async () => {
        try {
            const res = await readLinkWhitelist() as any;
            if (res?.code === 0) {
                this.setState({
                    whiteList: res?.data?.hostScheme || [],
                    whiteHttpList: cleanWhiteHttpList(
                        res?.data?.host?.concat(res?.data?.hostBrowser) || [],
                    ),
                });
            }
        } catch (error) {
            console.error('[SetFeedbackPost] load whitelist failed:', error);
        }
    };

    render() {
        const { app } = this.props;
        const platform = app?.platform || 'h5';

        if (!app?.platform) {
            return (
                <div
                    className={styles.loading}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                    }}
                >
                    <div>加载中...</div>
                </div>
            );
        }

        const commonProps = {
            setPostData: this.setPostData,
            onPublishPost: this.onPublishPost,
            loading: this.state.loading,
            whiteList: this.state.whiteList,
            whiteHttpList: this.state.whiteHttpList,
            editorReady: this.state.editorReady,
        };

        return (
            <div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (
                    <PC {...this.props} {...commonProps} />
                ) : (
                    <H5 {...this.props} {...commonProps} />
                )}
            </div>
        );
    }
}

export default withRouter(SetFeedbackPost);
